import PropTypes from 'prop-types'
import React from 'react'
import { uncontrollable } from 'uncontrollable'
import { FormattedMessage } from 'react-intl'
import Clipboard from 'react-clipboard.js'
import { convertToRaw } from 'draft-js'
import sweetalert from 'sweetalert2'
import {
  convertKanBoFormatToContentState
} from '../utils/convert'
import getCharCount from '../utils/getCharCount'
import redraft from 'redraft'
import { KanBoLink } from 'components/Link'
import User from 'components/ControlledUser'
import UnsavedChanges from 'components/UnsavedChanges'
import ClickOutside from 'components/ClickOutside'
import EditForm from './EditForm'
import editorClasses from '../Editor/Editor/Editor.module.scss'
import classes from './Content.module.scss'

const inline = {
  BOLD: (children, { key }) => <b key={key}>{children}</b>,
  ITALIC: (children, { key }) => <i key={key}>{children}</i>,
  UNDERLINE: (children, { key }) => <u key={key}>{children}</u>
}

const addBreaklines = children => children.map(child => [child, <br />])

const getListRenderer = (Tag, className) => (children, { depth, keys }) => {
  let compClassName = classes.list
  if (depth === 0) compClassName += ' ' + classes.rootList
  if (className) compClassName += ' ' + className

  return (
    <Tag
      key={keys[0]}
      className={compClassName}
    >
      {children.map((child, index) =>
        <li key={keys[index]} className={classes.listItem}>
          {child}
        </li>
      )}
    </Tag>
  )
}

const blocks = {
  unstyled: (children, { keys }) => <div key={keys[0]}>{addBreaklines(children)}</div>,
  blockquote:
    (children, { keys }) => children.map((child, index) => <blockquote key={keys[index]}>{child}</blockquote>),
  'unordered-list-item': getListRenderer('ul'),
  'ordered-list-item': getListRenderer('ol'),
  'unchecked-list-item': getListRenderer('ul', editorClasses.uncheckedList),
  'checked-list-item': getListRenderer('ul', editorClasses.checkedList)
}

const stopPropagation = (event) => { event.stopPropagation() }

const entities = {
  mention: (children, entity, { key }) => <User key={key} text defaultMenu id={entity.mention.get('id')} />,
  LINK: (children, entity, { key }) => {
    if (entity.url.indexOf('file:///') === 0) {
      return (
        <FormattedMessage id='LINK_COPIED_TO_CLIPBOARD'>
          {formattedMessage =>
            <Clipboard
              key={key}
              component='a'
              data-clipboard-text={entity.url.substr(8)}
              className={classes.pathLink}
              onClick={stopPropagation}
              onSuccess={() => {
                sweetalert({
                  type: 'success',
                  text: formattedMessage,
                  timer: 1000,
                  showConfirmButton: false
                }).catch(sweetalert.noop)
              }}
            >
              {children}
            </Clipboard>
          }
        </FormattedMessage>
      )
    } else {
      return (
        <KanBoLink
          href={entity.url}
          onClick={stopPropagation}
        >
          {children}
        </KanBoLink>
      )
    }
  }
}

export class RichTextContent extends React.Component {
  static propTypes = {
    unsavedValue: PropTypes.object,

    removeUnsavedChange: PropTypes.func,

    isEditing: PropTypes.bool.isRequired,
    onIsEditingChange: PropTypes.func.isRequired,

    content: PropTypes.object,
    noContentText: PropTypes.element,
    editable: PropTypes.bool,
    saveChanges: PropTypes.string,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    validate: PropTypes.func,
    onSubmit: PropTypes.func,
    className: PropTypes.string
  }

  constructor (props) {
    super(props)

    this.state = {
      raw: this.convertContent(props.content)
    }

    this.convertContent = this.convertContent.bind(this)
    this.close = this.close.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleDiscardClick = this.handleDiscardClick.bind(this)
    this.handleValidate = this.handleValidate.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentWillReceiveProps (nextProps) {
    if (nextProps.content !== this.props.content) {
      this.setState({
        raw: this.convertContent(nextProps.content)
      })
    }
  }

  convertContent (content) {
    if (!content) {
      return null
    }

    const contentState = convertKanBoFormatToContentState(content)

    return convertToRaw(contentState)
  }

  close () {
    this.props.onIsEditingChange(false)

    this.props.onClose && this.props.onClose()
  }

  handleClick () {
    if (this.props.editable && !this.props.isEditing) {
      this.props.onIsEditingChange(true)

      this.props.onOpen && this.props.onOpen()
    }
  }

  handleDiscardClick (event) {
    event.preventDefault()
    event.stopPropagation()

    const { saveChanges, removeUnsavedChange } = this.props

    removeUnsavedChange({
      id: saveChanges
    })
  }

  handleValidate ({ content }) {
    const { validate } = this.props

    if (validate) {
      const error = validate(content)
      if (error) return { content: error }
    }

    return {}
  }

  handleSubmit ({ content }) {
    const { onSubmit } = this.props

    return onSubmit(content)
  }

  renderContent = () => {
    const {
      content,
      noContentText
    } = this.props
    const {
      raw
    } = this.state

    if (getCharCount(content) > 0) return redraft(raw, { inline, blocks, entities })

    if (noContentText) return <span className={classes.noContentText}>{noContentText}</span>

    return null
  }

  render () {
    const {
      unsavedValue,

      removeUnsavedChange,

      isEditing,

      content,
      noContentText,
      editable,
      saveChanges,
      onClose,
      validate,
      onSubmit,
      className,

      ...props
    } = this.props

    let compClassName = classes.richTextContent
    if (editable && !isEditing) compClassName += ' ' + classes.editable
    if (className) compClassName += ' ' + className

    return (
      <div
        className={compClassName}
        onClick={this.handleClick}
      >
        {!isEditing && this.renderContent()}
        {(!isEditing && unsavedValue) &&
          <UnsavedChanges onDiscardClick={this.handleDiscardClick} />
        }
        {isEditing &&
          <EditForm
            richTextEditorProps={{
              ...props,
              content,
              autoFocus: true,
              saveChanges,
              onBlur: this.close
            }}
            initialValues={{
              content
            }}
            validate={this.handleValidate}
            onSubmit={this.handleSubmit}
            onSubmitSuccess={this.close}
          />
        }
      </div>
    )
  }
}

export default uncontrollable(
  RichTextContent,
  {
    isEditing: 'onIsEditingChange'
  }
)
